import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { AppVisibilityService } from '../../../../core/visibility/app-visibility.service';
import { StopInterval, visibleInterval } from '../../../../core/visibility/visible-interval';
import { Capacitor } from '@capacitor/core';
import { IonButton, IonIcon, IonRange, IonSpinner, Platform, ToastController } from '@ionic/angular/standalone';
import { Observable, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';

import { AppConfigService } from '../../../../../common/config/app.config.service';
import { Capability } from '../../../../../common/enums/capability.enum';
import { SubscriptionService } from '../../../../core/payment/services/subscription.service';
import { MediaService } from '../../../../core/service/media.service';
import { SyncService } from '../../sync/sync.service';
import { LogFactory } from '../../../../../common/services/logger/log-factory';
import { ToastLife } from '../../../../../common/enums/toast-life.enum';
import { MissingMediaMessage } from '../../../../../common/enums/storage-manager-message.enum';
import { FormatDurationPipe } from '../../pipes/format-duration.pipe';
import { AuthenticationService } from '../../../../../common/services/authentication/authentication.service';
import { BrandingApiService, UserBranding } from '../../services/branding-api.service';
import { DbService } from '../../services/db.service';
import {
  PauseReason,
  WorkoutService,
  WorkoutViewModel,
} from '../../services/workout.service';
import {
  activityDurationSec,
  activityYoutubeUrl,
} from '../../utils/activity.util';
import { mediaRestartFires } from '../../utils/media-restart.util';
import { youtubeThumbnailUrl } from '../../utils/youtube.util';

declare global {
  interface Window {
    YT?: {
      Player: new (
        el: HTMLElement,
        config: {
          events?: {
            onReady?: (ev: { target: YTPlayer }) => void;
            onStateChange?: (ev: { data: number }) => void;
          };
        },
      ) => YTPlayer;
      ready: (fn: () => void) => void;
    };
    onYouTubeIframeAPIReady?: () => void;
    /** E2E diagnostics for the audio track (the element is detached `new Audio()`,
     *  so it can't be reached via document.querySelector). Present only while the
     *  player panel is mounted. */
    __sequalPlayer?: {
      audioPresent: boolean;
      audioCurrentTime: number;
      audioPaused: boolean;
      audioDuration: number;
      audioLoop: boolean;
      muteVideo: boolean;
    };
  }
}
interface YTPlayer {
  playVideo(): void;
  pauseVideo(): void;
  seekTo(seconds: number, allowSeekAhead: boolean): void;
  mute(): void;
  unMute(): void;
  destroy(): void;
  getCurrentTime(): number;
  getDuration(): number;
  setPlaybackRate(suggestedRate: number): void;
}

// Ignore document clicks for this long after the panel expands, so the very
// click that opened/expanded it doesn't immediately minimise it again.
const OUTSIDE_CLICK_GUARD_MS = 300;

/** Minimum downward travel (px) for a swipe on the full panel to minimise it. */
const SWIPE_MINIMIZE_MIN_PX = 60;

/** A minimising swipe must be clearly vertical: its downward travel must be at
 *  least this many times its sideways travel, so a diagonal drag is ignored. */
const SWIPE_MINIMIZE_VERTICAL_RATIO = 1.5;

/** Touches that start on these are the control's own gesture (dragging a seek
 *  knob, selecting text), never a swipe to minimise. */
const SWIPE_IGNORE_SELECTOR = 'ion-range, input, textarea, select';

/** How long a first tap on the media waits to see whether it is really the
 *  first half of a double tap. Long enough for a comfortable double tap, short
 *  enough that a plain tap-to-pause still feels immediate. */
const DOUBLE_TAP_MS = 280;

/** Seconds a double tap (or an arrow key) moves the workout — the same step the
 *  ±10 buttons use, so all three routes agree. */
const SEEK_STEP_SEC = 10;

/** How long the ⏪/⏩ flash stays on the video after a double-tap seek. Without
 *  it a double tap has no affordance at all: the countdown moves, but nothing
 *  says the gesture was recognised. */
const SEEK_FLASH_MS = 600;

/** How often to check whether the YouTube API and iframe are ready. */
const YT_READY_POLL_MS = 100;

/** How often a playing YouTube player is read for the slider and the crop loop. */
const YT_PROGRESS_POLL_MS = 500;

/** The YouTube IFrame API's player states the player reacts to. */
/**
 * How far (seconds) a muted video may drift from the native track whose sound
 * it shows (ADR-0287) before it is moved to it. Tighter makes a visible stutter
 * every few seconds as the two clocks disagree slightly.
 */
const VIDEO_SOUNDTRACK_DRIFT_SEC = 0.3;
/** Shown by the local <video> before its first frame, in place of the browser's
 *  default poster: Android's web view draws a large grey play button, which
 *  reads as "tap to play" over a video that is only still loading. */
const VIDEO_BLANK_POSTER = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

const YT_STATE_ENDED = 0;
const YT_STATE_PLAYING = 1;
const YT_STATE_PAUSED = 2;

@Component({
  selector: 'app-workout-command-center',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, IonIcon, IonRange, FormatDurationPipe,
    IonButton,
    IonSpinner,
  ],
  templateUrl: './workout-command-center.component.html',
  styleUrls: ['./workout-command-center.component.scss'],
})
export class WorkoutCommandCenterComponent implements OnDestroy {
  workout$: Observable<WorkoutViewModel | null>;
  currentActivityImageUrl: string | null = null;
  /** "Up next" card data (next activity in the queue). */
  nextActivity: { name: string; summary: string } | null = null;
  nextImageUrl: string | null = null;
  private lastNextId: string | null = null;
  /** Key of the activity/group/sequence the current cover was built for, so we
   *  don't recreate the blob URL on every workout tick (which made it flicker). */
  private lastImageKey: string | null = null;

  readonly circleLength = 2 * Math.PI * 18;

  /** Brand shown as a footer/watermark inside the player. It reflects the
   *  creator's (coach/org) branding when present; only the logo and tagline fall
   *  back to Sequal, everything else stays empty (and hidden) when there is none.
   *  `appName` is the app identity, used only as the compact header-brand label
   *  fallback on short screens — the footer name has no Sequal fallback. */
  private readonly appConfig = inject(AppConfigService);
  protected readonly appName = this.appConfig.appName;
  private readonly sequalLogo = 'assets/icons/icon-96.webp';
  /** The footer stays hidden until branding resolves (brandingReady) so the
   *  Sequal mark never flashes before creator branding loads. All brand fields
   *  start empty and are set in loadBranding() — as either the full Sequal
   *  brand (mark + name + tagline) or one creator's own, never a mix. They are
   *  re-resolved whenever the content being played changes hands. */
  protected brandingReady = false;
  protected brandLogo = '';
  /** Blob URL of a resolved creator logo, tracked so it can be revoked. */
  private logoBlobUrl: string | null = null;
  protected brandName = '';
  protected brandTagline = '';
  /** Whose brand the footer is showing: the creator of the content being played,
   *  or null for the signed-in user's own. `undefined` until the first workout
   *  state has been read, so the first resolution always loads. */
  private brandCreatorId: string | null | undefined = undefined;
  /** The content the creator was last resolved from, so the per-tick emissions
   *  don't re-query the local DB every second. */
  private lastBrandKey: string | null = null;
  protected brandWebsite = '';
  protected brandInstagram = '';
  protected brandContactEmail = '';

  /** The expanded side-panel root, used to detect clicks outside it (desktop). */
  @ViewChild('panelEl') panelEl?: ElementRef<HTMLElement>;
  /** Timestamp of the last expand, to ignore the opening click (see guard). */
  private expandedAt = Date.now();
  @ViewChild('runnerVideo') runnerVideo?: ElementRef<HTMLVideoElement>;
  /** One re-resolve per error-free stretch of playback (see onVideoError). */
  private videoRecoveryUsed = false;
  @ViewChild('youtubeFrame') youtubeFrame?: ElementRef<HTMLIFrameElement>;
  @ViewChild('mediaStage') mediaStage?: ElementRef<HTMLElement>;

  /** Local-video seek slider state (player). */
  videoDurationSec = 0;
  videoCurrentSec = 0;

  /** YouTube seek slider state (polled — the YT API has no timeupdate event). */
  ytDurationSec = 0;
  ytCurrentSec = 0;
  /** Latest workout VM, cached so the YT callbacks (ENDED / progress poll) can read
   *  the crop window without re-subscribing. */
  private currentVm: WorkoutViewModel | null = null;
  private _minimized = false;
  get minimized() {
    return this._minimized;
  }
  /** When minimizing, save the current YT playback position. When reopening, restore it via start= param. */
  @Input() set minimized(val: boolean) {
    const wasMinimized = this._minimized;
    this._minimized = val;
    // A minimised player shows no video, so it must not keep the screen on, and
    // a step waiting for its video must not wait for it (ADR-0284).
    this.workout.setPlayerShown(!val);
    // Going to minimized: snapshot current playback time so we can resume from there on re-expand
    if (val && !wasMinimized && this.ytPlayer) {
      try {
        this.ytCurrentTime = Math.floor(this.ytPlayer.getCurrentTime() ?? 0);
      } catch {
        this.ytCurrentTime = 0;
      }
    }
    if (!val && wasMinimized) {
      // Re-expanded: reset the guard so the reopen click isn't treated as an
      // outside click that immediately minimises the panel again.
      this.expandedAt = Date.now();
      this.destroyYtPlayer();
      this.workout$.pipe(take(1)).subscribe((w) => {
        if (w?.isYoutubeVideo && w.videoUrl) {
          // Rebuild embed URL with start= so video resumes from where it was, not from 0.
          // Keep lastYoutubeRawUrl as the original URL so the subscription doesn't reset youtubeSafeUrl.
          let embedUrl = w.videoUrl.replace(/[?&]start=\d+/g, '');
          if (this.ytCurrentTime > 0) {
            const sep = embedUrl.includes('?') ? '&' : '?';
            embedUrl = `${embedUrl}${sep}start=${this.ytCurrentTime}`;
          }
          this.lastYoutubeRawUrl = w.videoUrl;
          this.youtubeSafeUrl =
            this.sanitizer.bypassSecurityTrustResourceUrl(embedUrl);
          setTimeout(() => {
            this.ensureYtPlayer(w.isPaused);
            this.syncVideoPauseState(w);
          }, 150);
        }
        this.cdr.markForCheck();
      });
    }
  }

  @Output() closePanel = new EventEmitter<void>();
  @Output() minimizePanel = new EventEmitter<void>();
  @Output() reopenPanel = new EventEmitter<void>();

  isMobile = false;
  /** Where a candidate minimising swipe began; null when the touch cannot be one. */
  private swipeStart: { x: number; y: number } | null = null;
  /** Cached sanitized YouTube URL so iframe src is stable across change detection. */
  youtubeSafeUrl: SafeResourceUrl | null = null;
  private lastYoutubeRawUrl: string | null = null;
  private sub = new Subscription();
  private ytPlayer: YTPlayer | null = null;
  /** Stops the player-ready poll (runs only while the app is visible). */
  private stopYtCheck: StopInterval | null = null;
  private ytProgressInterval: number | null = null;
  /** True while the YouTube player reports PLAYING: the only time its progress is read. */
  private ytPlaying = false;
  /** Bumped whenever the cover or the "up next" card changes owner, so a slow
   *  read that lands after a skip does not put the wrong picture up - or leak it. */
  private coverGeneration = 0;
  private nextGeneration = 0;
  /** Tracks whether TTS ducking is currently active, so late-initialized YT players get muted immediately. */
  private isDucking = false;
  /** Playback position (seconds) saved when minimizing, so re-expanding can resume from the same spot. */
  private ytCurrentTime = 0;
  /** Tracks set index to detect repeats of the same YouTube activity. */
  private prevSetIndex: number | null = null;
  /** Tracks rep index so a per-rep restart policy also reloads the video between reps. */
  private prevRepIndex: number | null = null;
  /** Previous emit's unit position for a LOCAL <video>, to catch unit boundaries. */
  private prevLocalUnit: { activityId: string; url: string; set: number; rep: number; rest: boolean } | null = null;
  /** Activity the current YouTube embed belongs to. Two consecutive activities can
   *  resolve to the SAME embed URL, and the URL alone can't tell them apart — without
   *  this the second one keeps playing mid-clip instead of starting over. */
  private lastYoutubeActivityId: string | null = null;
  /** Toggled false→true to force iframe DOM removal/re-add on repeat, reloading the video from 0. */
  ytFrameVisible = true;
  /** True once the YT iframe's content (https://www.youtube.com/embed/...) has finished loading.
   *  Instantiating `new YT.Player()` before the iframe's contentWindow is fully loaded triggers
   *  postMessage origin-mismatch warnings ("target origin youtube.com / recipient app-local…")
   *  because the iframe is still on about:blank when the YT widget API tries its first handshake.
   *  Gating ensureYtPlayer on this flag eliminates the warning without hiding any real error. */
  private ytIframeLoaded = false;

  protected onYtIframeLoad(): void {
    this.ytIframeLoaded = true;
  }

  private workout = inject(WorkoutService);
  private db = inject(DbService);
  private cdr = inject(ChangeDetectorRef);
  private readonly visibility = inject(AppVisibilityService);
  private sanitizer = inject(DomSanitizer);
  private platform = inject(Platform);
  private brandingApi = inject(BrandingApiService);
  private auth = inject(AuthenticationService);
  private subscription = inject(SubscriptionService);
  private media = inject(MediaService);
  private sync = inject(SyncService);
  private toastCtrl = inject(ToastController);
  private readonly logger = inject(LogFactory).createLogger(WorkoutCommandCenterComponent);

  readonly MissingMedia = MissingMediaMessage;
  /** True once a download attempt has proved the cloud has not got it either. */
  missingMediaGone = false;
  missingMediaBusy = false;
  /** Activity ids the user chose to run without. Cleared when the workout ends. */
  private readonly dismissedMissing = new Set<string>();

  /** Whether to draw the missing-media card for the activity on screen. */
  showMissingMedia(w: { mediaMissing?: boolean; activityId?: string }): boolean {
    return !!w.mediaMissing && !!w.activityId && !this.dismissedMissing.has(w.activityId);
  }

  /**
   * Fetch what this exercise is missing.
   *
   * A download that comes back still-missing is not a failure to retry — it means
   * the cloud has not got it either, so the offer changes from "download" to
   * "remove the dead reference".
   */
  async downloadMissingMedia(activityId: string | undefined): Promise<void> {
    if (!activityId || this.missingMediaBusy) return;
    this.missingMediaBusy = true;
    this.cdr.markForCheck();
    try {
      const stillMissing = await this.sync.downloadMediaForItems([activityId]);
      if (stillMissing.length) {
        this.missingMediaGone = true;
      } else {
        // Re-resolve so the video appears without the user restarting the set.
        this.dismissedMissing.delete(activityId);
        await this.workout.refreshCurrentMedia();
      }
    } catch (err) {
      this.logger.error('Could not download this exercise’s media', err, { activityId });
      await this.toast(MissingMediaMessage.DownloadFailed);
    } finally {
      this.missingMediaBusy = false;
      this.cdr.markForCheck();
    }
  }

  /** Run this exercise without its media, just for now. Changes nothing stored. */
  dismissMissingMedia(activityId: string | undefined): void {
    if (activityId) this.dismissedMissing.add(activityId);
    this.missingMediaGone = false;
    this.cdr.markForCheck();
  }

  /**
   * Detach media that no longer exists anywhere.
   *
   * Offered only after a download has shown the cloud has not got it, because
   * this is a content edit: the exercise becomes a plain timed one everywhere,
   * not just here. Deleting the exercise itself stays a separate, later choice —
   * the user may well still want it.
   */
  async removeMissingMedia(activityId: string | undefined): Promise<void> {
    if (!activityId || this.missingMediaBusy) return;
    this.missingMediaBusy = true;
    this.cdr.markForCheck();
    try {
      await this.db.clearActivityMedia(activityId);
      this.dismissedMissing.add(activityId);
      this.missingMediaGone = false;
      await this.toast(MissingMediaMessage.Removed);
    } catch (err) {
      this.logger.error('Could not remove the missing media reference', err, { activityId });
    } finally {
      this.missingMediaBusy = false;
      this.cdr.markForCheck();
    }
  }

  private async toast(message: string): Promise<void> {
    const t = await this.toastCtrl.create({ message, duration: ToastLife.MEDIUM, position: 'top' });
    await t.present();
  }

  constructor() {
    // Plain DOM listeners rather than @HostListener('document:…'): a host
    // listener schedules change detection for every key press and click anywhere
    // in the app while the player exists, although both handlers ignore nearly
    // all of them. The handlers mark the view themselves when they act.
    const onKeydown = (event: KeyboardEvent) => this.onDocumentKeydown(event);
    const onClick = (event: MouseEvent) => this.onDocumentClick(event);
    document.addEventListener('keydown', onKeydown);
    document.addEventListener('click', onClick);
    inject(DestroyRef).onDestroy(() => {
      document.removeEventListener('keydown', onKeydown);
      document.removeEventListener('click', onClick);
    });

    this.isMobile =
      typeof window !== 'undefined' &&
      window.matchMedia('(max-width: 820px)').matches;
    const q = window.matchMedia('(max-width: 820px)');
    const onViewportChange = () => {
      this.isMobile = q.matches;
      this.cdr.markForCheck();
    };
    q.addEventListener?.('change', onViewportChange);
    // The player is created for every workout; a listener left on the media
    // query kept each destroyed one alive (ADR-0284).
    inject(DestroyRef).onDestroy(() => q.removeEventListener?.('change', onViewportChange));
    this.workout.setPlayerShown(!this._minimized);
    this.workout$ = this.workout.workout$;
    // "Up next" card: rebuild only when the queue head changes (avoid per-tick churn).
    this.sub.add(
      this.workout$.subscribe(async (w) => {
        const nextId = w?.activityQueue?.[0]?.activityId ?? null;
        if (nextId === this.lastNextId) return;
        this.lastNextId = nextId;
        const generation = ++this.nextGeneration;
        if (this.nextImageUrl?.startsWith('blob:'))
          URL.revokeObjectURL(this.nextImageUrl);
        this.nextImageUrl = null;
        this.nextActivity = null;
        if (nextId) {
          const a = await this.db.getActivity(nextId);
          if (a && generation === this.nextGeneration) {
            const totalSec = activityDurationSec(a);
            const mm = Math.floor(totalSec / 60);
            const ss = Math.round(totalSec % 60).toString().padStart(2, '0');
            this.nextActivity = {
              name: a.name,
              summary: mm > 0 ? `${mm}:${ss}` : `${ss}s`,
            };
            const blob = await this.db.getImageBlobForActivity(a);
            const url = blob
              ? URL.createObjectURL(blob)
              : (youtubeThumbnailUrl(activityYoutubeUrl(a)) ?? null);
            // Skipped again while the picture was read: it belongs to nobody.
            if (generation !== this.nextGeneration) {
              if (url?.startsWith('blob:')) URL.revokeObjectURL(url);
              return;
            }
            this.nextImageUrl = url;
          }
        }
        this.cdr.markForCheck();
      }),
    );
    this.sub.add(
      this.workout$.subscribe(async (w) => {
        if (!w) {
          this.lastImageKey = null;
          return;
        }
        // The workout state emits on every timer tick; only rebuild the cover
        // when the underlying activity/group/sequence actually changes, else the
        // <img> src churns each second and flickers (most visible in web split view).
        const imageKey = `${w.activityId}|${w.contextGroupId ?? ''}|${w.contextSequenceId ?? ''}`;
        if (imageKey === this.lastImageKey) return;
        this.lastImageKey = imageKey;
        const generation = ++this.coverGeneration;
        if (this.currentActivityImageUrl) {
          URL.revokeObjectURL(this.currentActivityImageUrl);
          this.currentActivityImageUrl = null;
        }

        const activity = await this.db.getActivity(w.activityId);
        let blob = await this.db.getImageBlobForActivity(activity ?? undefined);

        if (!blob && w.contextGroupId) {
          const group = await this.db.getGroup(w.contextGroupId);
          if (group) blob = await this.db.getImageBlobForGroup(group);
        }

        if (!blob && w.contextSequenceId) {
          const seq = await this.db.getSequence(w.contextSequenceId);
          if (seq) blob = await this.db.getImageBlobForSequence(seq);
        }

        // Skipped again while the cover was read: drop it rather than show it
        // over the wrong activity, or leak it.
        if (generation !== this.coverGeneration) return;
        if (blob) {
          this.currentActivityImageUrl = URL.createObjectURL(blob);
        }
        this.cdr.markForCheck();
      }),
    );
    this.sub.add(
      this.workout$.subscribe((w) => {
        this.currentVm = w;
        if (!w?.videoUrl) {
          this.youtubeSafeUrl = null;
          this.lastYoutubeRawUrl = null;
          this.lastYoutubeActivityId = null;
          this.prevSetIndex = null;
          this.prevRepIndex = null;
          this.prevLocalUnit = null;
          this.destroyYtPlayer();
          return;
        }
        if (w.isYoutubeVideo && w.videoUrl) {
          // New media OR a new activity that happens to reuse the same embed URL —
          // both mean "start this video over", not "continue where it was".
          const isNewUrl =
            w.videoUrl !== this.lastYoutubeRawUrl ||
            w.activityId !== this.lastYoutubeActivityId;
          // A unit boundary the restart policy fires on: any set change, plus rep
          // changes when the policy is per-rep (a rep advance keeps setIndex fixed).
          const isRepeat =
            !isNewUrl &&
            this.prevSetIndex !== null &&
            (w.setIndex !== this.prevSetIndex ||
              (w.videoRestart != null &&
                mediaRestartFires('rep', w.videoRestart) &&
                w.repIndex !== this.prevRepIndex));
          this.prevSetIndex = w.setIndex ?? null;
          this.prevRepIndex = w.repIndex ?? null;

          if (isNewUrl) {
            // New video — reset everything. The previous YT.Player is bound to the
            // old <iframe>; if we leave it alive, ensureYtPlayer() short-circuits on
            // `this.ytPlayer != null` and never binds the new iframe, so play/pause
            // commands disappear into the void. Tear it down before swapping the URL.
            this.destroyYtPlayer();
            this.ytFrameVisible = false;
            this.lastYoutubeRawUrl = w.videoUrl;
            this.lastYoutubeActivityId = w.activityId;
            this.ytCurrentTime = 0;
            this.youtubeSafeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
              w.videoUrl,
            );
            this.cdr.markForCheck();
            setTimeout(() => {
              this.ytFrameVisible = true;
              this.cdr.markForCheck();
              setTimeout(() => this.ensureYtPlayer(w.isPaused), 150);
            }, 50);
            return; // ensureYtPlayer scheduled above; skip the trailing call below
          } else if (isRepeat) {
            // Same video, new set — remove iframe from DOM then re-add to reload from position 0
            this.ytCurrentTime = 0;
            this.destroyYtPlayer();
            this.ytFrameVisible = false;
            this.cdr.markForCheck();
            setTimeout(() => {
              this.ytFrameVisible = true;
              this.cdr.markForCheck();
              setTimeout(() => this.ensureYtPlayer(false), 150);
            }, 50);
            return; // ensureYtPlayer called in timeout above
          }
        } else if (!w.isYoutubeVideo) {
          this.youtubeSafeUrl = null;
          this.lastYoutubeRawUrl = null;
          this.lastYoutubeActivityId = null;
          this.prevSetIndex = null;
          this.prevRepIndex = null;
          this.maybeRestartLocalVideo(w);
        }
        setTimeout(() => this.syncVideoPauseState(w), 0);
        if (w.isYoutubeVideo) {
          this.ensureYtPlayer(w.isPaused);
        } else {
          this.destroyYtPlayer();
        }
      }),
    );
    this.installE2eAudioProbe();
    this.initPlayerBackButton();
    // ±10 s from the lock screen moves the video with the countdown, exactly as
    // the player's own buttons do.
    this.sub.add(this.workout.nudgeRequests$.subscribe((delta) => this.nudge(delta)));
    // Off screen (ADR-0284): a local video is paused, as iOS does by itself and
    // Android's web view does not; back on screen it plays again if the workout
    // is running. The YouTube slider is read only while it can be seen.
    // YouTube allows no background play (ADR-0287): out of sight it pauses at
    // once, whatever the web view would do by itself; the workout pauses with it.
    this.sub.add(
      this.visibility.visible$.subscribe((visible) => {
        const el = this.runnerVideo?.nativeElement;
        const w = this.currentVm;
        if (el && w?.videoUrl && !w.isYoutubeVideo) {
          if (!visible) el.pause();
          else if (!w.isPaused || this.workout.awaitingMediaStart) void el.play().catch(() => undefined);
        }
        if (!visible && w?.isYoutubeVideo) this.pauseYoutube();
        this.syncYtProgressPolling();
      }),
    );
    // Whose brand to show follows the CONTENT, not the account: a routine
    // published by another coach carries their studio, not the reader's. The
    // BehaviorSubject replays the current state on subscribe, so this also does
    // the initial load.
    this.sub.add(
      this.workout$.subscribe(async (w) => {
        const key = w
          ? `${w.contextSequenceId ?? ''}|${w.contextGroupId ?? ''}|${w.activityId}`
          : null;
        if (key === this.lastBrandKey) return;
        this.lastBrandKey = key;
        const creatorId = w ? await this.creatorOf(w) : null;
        if (creatorId === this.brandCreatorId) return;
        this.brandCreatorId = creatorId;
        await this.reloadBranding();
      }),
    );
    // Reflect live branding edits without a stop/start.
    this.sub.add(
      this.brandingApi.changed$.subscribe(() => void this.reloadBranding()),
    );
    this.sub.add(
      this.workout.ducking$.subscribe((isDucking) => {
        this.isDucking = isDucking;
        if (!this.ytPlayer) return;
        try {
          if (isDucking) this.ytPlayer.mute();
          // Never unmute a video whose activity configuration demands silence —
          // otherwise the first spoken cue's ducking cycle turns it audible.
          else if (!this.currentVm?.muteVideo) this.ytPlayer.unMute();
        } catch {
          /* ignore */
        }
      }),
    );
  }

  ngOnDestroy(): void {
    if (this.currentActivityImageUrl) {
      URL.revokeObjectURL(this.currentActivityImageUrl);
    }
    if (this.nextImageUrl?.startsWith('blob:'))
      URL.revokeObjectURL(this.nextImageUrl);
    if (this.logoBlobUrl) this.media.revokeBlobUrl(this.logoBlobUrl);
    if (this.tapTimer != null) clearTimeout(this.tapTimer);
    if (this.seekFlashTimer != null) clearTimeout(this.seekFlashTimer);
    this.destroyYtPlayer();
    this.stopYtCheck?.();
    this.sub.unsubscribe();
    if (typeof window !== 'undefined') delete window.__sequalPlayer;
  }

  /**
   * Publish live audio-track diagnostics on `window.__sequalPlayer` for E2E
   * specs. The audio element is a detached `new Audio()` (never in the DOM), so
   * tests can't reach it with `document.querySelector('audio')`; each property is
   * a live getter off the service so a read always reflects the current state.
   * Inert in production — nothing reads it unless a test does.
   */
  private installE2eAudioProbe(): void {
    if (typeof window === 'undefined') return;
    const workout = this.workout;
    window.__sequalPlayer = {
      get audioPresent() {
        return !!workout.audioElement;
      },
      get audioCurrentTime() {
        return workout.audioElement?.currentTime ?? 0;
      },
      get audioPaused() {
        return workout.audioElement?.paused ?? true;
      },
      get audioDuration() {
        const d = workout.audioElement?.duration;
        return Number.isFinite(d) ? (d as number) : 0;
      },
      get audioLoop() {
        return !!workout.audioElement?.loop;
      },
      get muteVideo() {
        return !!workout.currentState?.muteVideo;
      },
    };
  }

  /**
   * Whose work is playing.
   *
   * Every row of a copy — a community download or a signed .fitpack import
   * alike — is stamped with the id of the user who created it, so the outermost
   * thing being played answers the question: the sequence if there is one, else
   * the group, else the activity. Null for content with no stamp (your own, and
   * the Sequal catalog).
   */
  private async creatorOf(w: WorkoutViewModel): Promise<string | null> {
    if (w.contextSequenceId) {
      return (await this.db.getSequence(w.contextSequenceId))?.createdByUserId ?? null;
    }
    if (w.contextGroupId) {
      return (await this.db.getGroup(w.contextGroupId))?.createdByUserId ?? null;
    }
    return (await this.db.getActivity(w.activityId))?.createdByUserId ?? null;
  }

  /**
   * Load the branding for the player watermark: the brand of whoever created
   * the content being played.
   *
   * That distinction is the whole point — a client playing their coach's
   * routine has to see the COACH's studio, not their own (showing the reader's
   * own brand on somebody else's work was the bug). Their own content, and any
   * content with no creator stamp, uses their own effective (org-wins) brand.
   *
   * The Sequal fallback is all-or-nothing: once any creator field is set we drop
   * every Sequal default (no Sequal mark, no Sequal tagline) so the two brands
   * never mix. Blank fields simply stay hidden.
   */
  private async loadBranding(): Promise<void> {
    const creatorId = this.brandCreatorId ?? null;
    if (creatorId && creatorId !== this.auth.getUserId()) {
      await this.loadCreatorBranding(creatorId);
      return;
    }
    // Their own content. No capability → resolve synchronously (before first
    // render) to the Sequal brand, so most users see it immediately with no flash.
    if (!this.subscription.hasCapability(Capability.BRANDING)) {
      this.useSequalBrand();
      return;
    }
    try {
      const b = await this.brandingApi.getEffective();
      if (!this.hasBranding(b)) {
        this.useSequalBrand();
        return;
      }
      this.applyBrand(b);
      if (b.logoMediaId) await this.applyCreatorLogo(b.logoMediaId);
    } catch {
      this.useSequalBrand();
    }
  }

  /**
   * The brand of another user whose content this is. Not gated on the reader's
   * plan — the creator is the one who paid for a brand, and the server decides
   * whether this reader may see it (they have imported their share, or the
   * creator publishes). Anything else keeps the neutral Sequal brand, which is
   * never worse than showing the wrong studio.
   */
  private async loadCreatorBranding(creatorId: string): Promise<void> {
    try {
      const b = await this.brandingApi.getForCreator(creatorId);
      if (!this.hasBranding(b)) {
        this.useSequalBrand();
        return;
      }
      this.applyBrand(b);
      // Their logo comes back as a ready-to-fetch URL: a reader can do nothing
      // with somebody else's media id.
      if (b.logoUrl) await this.applyLogoUrl(b.logoUrl);
    } catch {
      this.useSequalBrand();
    }
  }

  /** Show ONLY this brand's values — see the all-or-nothing rule above. */
  private applyBrand(b: UserBranding): void {
    this.brandName = b.businessName ?? '';
    this.brandTagline = b.tagline ?? '';
    this.brandWebsite = b.websiteUrl ?? '';
    this.brandInstagram = b.socialHandles?.['instagram'] ?? '';
    this.brandContactEmail = b.contactEmail ?? '';
    this.brandLogo = ''; // no Sequal mark in creator mode; set only if theirs resolves
    this.brandingReady = true;
    this.cdr.markForCheck();
  }

  /** The neutral Sequal brand shown when there is no creator branding: mark,
   *  app name and tagline (an all-or-nothing set — never mixed with creator fields). */
  private useSequalBrand(): void {
    this.brandLogo = this.sequalLogo;
    this.brandName = this.appName;
    this.brandTagline = this.appConfig.appTagline;
    this.brandWebsite = '';
    this.brandInstagram = '';
    this.brandContactEmail = '';
    this.brandingReady = true;
    this.cdr.markForCheck();
  }

  /** Re-fetch branding after the user edits it, so a running workout reflects the
   *  change without a stop/start. Releases the old resolved logo first. */
  private async reloadBranding(): Promise<void> {
    if (this.logoBlobUrl) {
      this.media.revokeBlobUrl(this.logoBlobUrl);
      this.logoBlobUrl = null;
    }
    await this.loadBranding();
  }

  /** True when this brand has any field worth showing. Generic so a caller with
   *  a richer brand shape (the creator read, which adds a logo URL) keeps it. */
  private hasBranding<T extends UserBranding>(b: T | null): b is T {
    return !!(
      b &&
      (b.businessName ||
        b.tagline ||
        b.logoMediaId ||
        b.websiteUrl ||
        b.contactEmail ||
        b.socialHandles?.['instagram'])
    );
  }

  /**
   * Resolve the user's OWN logo id to a viewable image and use it in place of the
   * Sequal mark. Keeps the Sequal fallback when it can't be resolved (e.g. an org
   * logo owned by another admin, or a transient fetch failure).
   */
  private async applyCreatorLogo(logoMediaId: string): Promise<void> {
    const url = await this.brandingApi.getMediaUrl(logoMediaId).catch(() => null);
    if (url) await this.applyLogoUrl(url);
  }

  /** Fetch a logo URL (own media-access URL, or another creator's short-lived
   *  one) and show the bytes. Silent on failure — the mark is decoration, and
   *  the brand text is already up. */
  private async applyLogoUrl(url: string): Promise<void> {
    try {
      this.logoBlobUrl = await this.media.getMediaBlobUrl(url);
      this.brandLogo = this.logoBlobUrl;
      this.cdr.markForCheck();
    } catch {
      // Unresolvable — keep the brand text without a mark.
    }
  }

  hasVisualMedia(w: WorkoutViewModel): boolean {
    // Media is now type-agnostic: show the stage whenever there's a video (local or
    // YouTube) or a cover image to display.
    return !!w.videoUrl || !!this.currentActivityImageUrl;
  }

  /** Square cover for an audio-kind YouTube activity: prefer the user's uploaded
   *  activity image, else the bar-free YouTube poster (mqdefault, object-fit:cover). */
  youtubeAudioCoverUrl(w: WorkoutViewModel): string | null {
    return this.currentActivityImageUrl ?? youtubeThumbnailUrl(w.videoUrl);
  }

  /** For YouTube iframe use youtubeSafeUrl (cached). For other callers, sanitize on demand. */
  getVideoSafeUrl(url: string | null | undefined): SafeResourceUrl | null {
    if (!url) return null;
    if (url === this.lastYoutubeRawUrl && this.youtubeSafeUrl)
      return this.youtubeSafeUrl;
    return this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }

  /** Local <video>: seek back to the crop-in point when a unit boundary the restart
   *  policy fires on is crossed (rep/set advance, or a between-sets rest ending).
   *  The native loop only rewinds a clip that ENDED — a boundary mid-clip must
   *  restart explicitly. Runs before syncVideoPauseState() (re)plays the element. */
  private maybeRestartLocalVideo(w: WorkoutViewModel): void {
    const prev = this.prevLocalUnit;
    this.prevLocalUnit = w.videoUrl
      ? { activityId: w.activityId, url: w.videoUrl, set: w.setIndex ?? 1, rep: w.repIndex ?? 1, rest: !!w.isRest }
      : null;
    if (!w.videoUrl || w.isRest || !prev || prev.url !== w.videoUrl) return;
    const el = this.runnerVideo?.nativeElement;
    if (!el) return;
    const seekToCropStart = () => {
      try {
        el.currentTime = this.videoCropStart(w);
      } catch {
        /* seek before metadata ready — the load itself starts at the crop-in point */
      }
    };
    // A DIFFERENT activity reusing the same media URL (a repeated exercise, a shared
    // remote clip). The <video> src never changes, so nothing reloads the element —
    // restart it outright. This is a new activity, not a rep/set boundary the restart
    // policy governs, so the policy does not get a say.
    if (prev.activityId !== w.activityId) {
      seekToCropStart();
      return;
    }
    const setChanged = prev.set !== (w.setIndex ?? 1);
    const repChanged = prev.rep !== (w.repIndex ?? 1);
    if (!prev.rest && !setChanged && !repChanged) return;
    const boundary = prev.rest || setChanged ? 'set' : 'rep';
    if (w.videoRestart == null || !mediaRestartFires(boundary, w.videoRestart)) return;
    seekToCropStart();
  }

  private syncVideoPauseState(w: WorkoutViewModel): void {
    if (w.isYoutubeVideo && this.ytPlayer) {
      try {
        this.applyYtRate(this.ytPlayer);
        // A step that is starting plays, so its PLAYING releases the start hold.
        // Out of sight it never plays, not even to release the hold (ADR-0287).
        if ((w.isPaused && !w.isStarting) || !this.visibility.isVisible()) this.ytPlayer.pauseVideo();
        else this.ytPlayer.playVideo();
      } catch {
        // ignore
      }
    } else if (!w.isYoutubeVideo) {
      const el = this.runnerVideo?.nativeElement;
      if (el) {
        this.applyVideoMute(el, w);
        this.applyLocalVideoRate(el, w);
        // A "paused" state that's really just the start-of-activity media hold must
        // still play, so the video can fire `playing` and release the clock. Off
        // screen it stays paused (ADR-0284): this runs on every update, and
        // played the video again a second after the app was hidden.
        if ((w.isPaused && !this.workout.awaitingMediaStart) || !this.visibility.isVisible()) el.pause();
        else {
          this.followVideoSoundtrack(el, w);
          el.play().catch(() => {});
        }
      }
    }
  }

  /**
   * A video whose sound plays on the native track (ADR-0287) shows that
   * track's moment: the track runs on while the video cannot be seen, and the
   * two clocks drift apart slightly while both run.
   */
  private followVideoSoundtrack(el: HTMLVideoElement, w: WorkoutViewModel): void {
    if (!w.videoSoundNative) return;
    const t = this.workout.videoSoundtrackTimeSec;
    if (t == null || Math.abs(el.currentTime - t) <= VIDEO_SOUNDTRACK_DRIFT_SEC) return;
    try {
      el.currentTime = t;
    } catch {
      // Before its metadata: the next update moves it.
    }
  }

  private pauseYoutube(): void {
    try {
      this.ytPlayer?.pauseVideo();
    } catch {
      // Player mid-teardown.
    }
  }

  /** Per-media playback speed on the YouTube player (idempotent, cheap to re-assert —
   *  YT resets the rate on a fresh player and sometimes on cue). */
  private applyYtRate(player: YTPlayer): void {
    try {
      player.setPlaybackRate(this.currentVm?.mediaPlaybackRate ?? 1);
    } catch {
      /* player mid-teardown */
    }
  }

  /** Per-media playback speed on the local <video> (re-asserted on every sync — the
   *  engine can reset the rate when the element reloads). Pitch stays natural. */
  private applyLocalVideoRate(
    el: HTMLVideoElement,
    w: Pick<WorkoutViewModel, 'mediaPlaybackRate'> | null | undefined,
  ): void {
    const rate = w?.mediaPlaybackRate ?? 1;
    if (el.playbackRate !== rate) el.playbackRate = rate;
    el.preservesPitch = true;
    (el as HTMLVideoElement & { webkitPreservesPitch?: boolean }).webkitPreservesPitch = true;
  }

  /** Enforce the activity's video mute on the <video> element itself. The [muted] template
   *  binding only re-applies when its expression value changes, so an element the
   *  browser unmuted (native fullscreen controls) or reused across activities can end
   *  up audible with the flag still true; re-asserting on every sync/play keeps the
   *  video silent whenever the activity's saved setting demands it. */
  private applyVideoMute(
    el: HTMLVideoElement,
    w: Pick<WorkoutViewModel, 'muteVideo' | 'videoVolume' | 'videoSoundNative'> | null | undefined,
  ): void {
    // Its sound plays on the native track instead (ADR-0287).
    const mute = !!w?.muteVideo || !!w?.videoSoundNative;
    el.defaultMuted = mute;
    if (el.muted !== mute) el.muted = mute;
    // Level the video's own soundtrack the same way the audio channels are
    // levelled (ADR-0213). Mute stays a separate concern — it is what actually
    // works everywhere, and volume 0 is not a substitute for it.
    const volume = w?.videoVolume;
    if (typeof volume === 'number' && Number.isFinite(volume)) {
      el.volume = Math.min(1, Math.max(0, volume));
    }
  }

  private ensureYtPlayer(_startPaused: boolean): void {
    if (this.ytPlayer) return;
    if (this.stopYtCheck != null) return; // already polling
    const tryCreate = () => {
      if (this.ytPlayer) return; // guard against double-creation from multiple YT.ready queues
      if (
        !window.YT ||
        !this.youtubeFrame?.nativeElement ||
        !this.ytIframeLoaded
      )
        return;
      this.stopYtCheck?.();
      this.stopYtCheck = null;
      try {
        this.ytPlayer = new window.YT!.Player(this.youtubeFrame.nativeElement, {
          events: {
            onReady: (ev: { target: YTPlayer }) => {
              // Mute while TTS is ducking, and permanently when the activity plays a
              // voice-over overlay (the URL's mute=1 covers first load; a player
              // rebuilt on a set-repeat must re-assert it here).
              if (this.isDucking || this.currentVm?.muteVideo) ev.target.mute();
              // Per-media playback speed — set here so first load AND every per-rep/
              // set iframe rebuild get it (YT resets the rate on a fresh player).
              this.applyYtRate(ev.target);
              // Plays while the workout runs or is starting (onStateChange(PLAYING) →
              // resume() starts the timer). A step the user paused stays paused: a
              // rebuilt player (the panel reopened) autoplaying under a paused clock
              // put the two out of step.
              const vm = this.currentVm;
              if ((vm?.isPaused && !vm.isStarting) || !this.visibility.isVisible()) ev.target.pauseVideo();
              else ev.target.playVideo();
            },
            onStateChange: (ev: { data: number }) => {
              // The slider and the crop loop are read only while it plays.
              this.ytPlaying = ev.data === YT_STATE_PLAYING;
              this.syncYtProgressPolling();
              if (ev.data === YT_STATE_ENDED) {
                // Loop: seek to the crop-in point (0 when uncropped) then play.
                try {
                  this.ytPlayer?.seekTo(this.ytCropStart(), true);
                  if (this.ytPlayer) this.applyYtRate(this.ytPlayer); // YT may reset the rate on cue
                  this.ytPlayer?.playVideo();
                } catch {
                  /* ignore */
                }
                return;
              }
              // The player drives the clock both ways, but a PLAYING that lands
              // after the user paused does not undo their pause (ADR-0284).
              if (ev.data === YT_STATE_PLAYING) this.workout.onMediaPlaying();
              else if (ev.data === YT_STATE_PAUSED) void this.workout.pause(PauseReason.MEDIA);
            },
          },
        });
      } catch {
        this.ytPlayer = null;
      }
    };
    // Poll every 100 ms until window.YT is loaded, the iframe element is in the DOM,
    // AND the iframe has finished loading its YT embed (ytIframeLoaded). Initialising
    // `new YT.Player()` before the iframe contentWindow finishes loading triggers
    // postMessage origin-mismatch warnings.
    // Setup only, so it waits while the app is hidden (the progress poll below
    // stays a plain interval: it also enforces the crop loop on playing audio).
    this.stopYtCheck = visibleInterval(this.visibility, () => {
      if (
        window.YT &&
        this.youtubeFrame?.nativeElement &&
        this.ytIframeLoaded
      ) {
        window.YT.ready(tryCreate);
      }
    }, YT_READY_POLL_MS);
    if (window.YT && this.youtubeFrame?.nativeElement && this.ytIframeLoaded) {
      window.YT.ready(tryCreate);
    }
  }

  private destroyYtPlayer(): void {
    if (this.ytPlayer) {
      try {
        this.ytPlayer.destroy();
      } catch {
        // ignore
      }
      this.ytPlayer = null;
    }
    this.stopYtCheck?.();
    this.stopYtCheck = null;
    this.ytPlaying = false;
    this.stopYtProgressPolling();
    this.ytCurrentSec = 0;
    this.ytDurationSec = 0;
    // Next iframe to render will start at about:blank; gate the rebind on its load event.
    this.ytIframeLoaded = false;
  }

  /**
   * Reads the YouTube player for its time and length (the IFrame API emits no
   * timeupdate) - only while it plays (ADR-0284): a paused or ended player has
   * nothing new to say, and polling it woke the phone twice a second for
   * nothing. Hidden, it is read only to hold a crop window (a YouTube audio
   * activity can keep playing with the screen off); the slider is not drawn.
   */
  private syncYtProgressPolling(): void {
    const needed =
      !!this.ytPlayer && this.ytPlaying && (this.visibility.isVisible() || this.ytCropEnd() > 0);
    if (needed) this.startYtProgressPolling();
    else this.stopYtProgressPolling();
  }

  private startYtProgressPolling(): void {
    if (this.ytProgressInterval != null) return;
    this.ytProgressInterval = window.setInterval(() => {
      if (!this.ytPlayer) return;
      try {
        this.ytDurationSec = this.ytPlayer.getDuration() ?? 0;
        this.ytCurrentSec = this.ytPlayer.getCurrentTime() ?? 0;
        // Honour the crop window: loop back to the crop-in point at crop-out (the
        // YT start= param can't express an end, so we enforce it here).
        const end = this.ytCropEnd();
        if (end > 0 && this.ytCurrentSec >= end - 0.3) {
          this.ytPlayer.seekTo(this.ytCropStart(), true);
        }
        if (this.visibility.isVisible()) this.cdr.markForCheck();
      } catch {
        /* ignore until the player is ready */
      }
    }, YT_PROGRESS_POLL_MS) as unknown as number;
  }

  private stopYtProgressPolling(): void {
    if (this.ytProgressInterval != null) {
      clearInterval(this.ytProgressInterval);
      this.ytProgressInterval = null;
    }
  }

  /** Crop-in point (seconds) for the current YouTube activity, 0 when uncropped. */
  private ytCropStart(): number {
    return Math.max(0, this.currentVm?.videoCropStartSec ?? 0);
  }
  /** Crop-out point (seconds) for the current YouTube activity, or 0 when there is
   *  no effective trim (no crop-end, or it covers the whole clip). */
  private ytCropEnd(): number {
    const w = this.currentVm;
    if (!w?.isYoutubeVideo) return 0;
    const full = this.ytDurationSec || 0;
    const end = w.videoCropEndSec ?? null;
    if (end == null || end <= 0) return 0;
    return full > 0 ? Math.min(end, full) : end;
  }

  /** Seek the YouTube video via the slider, keeping the activity clock aligned with
   *  the media position. A seek that crosses a unit boundary lets the service load
   *  the next unit instead of forcing the previous clip back to the selected time. */
  seekYoutube(ev: Event): void {
    const val = Number((ev as CustomEvent<{ value: number }>).detail?.value);
    if (!Number.isFinite(val) || !this.ytPlayer) return;
    try {
      const current = this.ytPlayer.getCurrentTime() ?? this.ytCurrentSec;
      if (this.updateCountdownForMediaSeek(current, val)) {
        this.cdr.markForCheck();
        return;
      }
      this.ytPlayer.seekTo(val, true);
      this.ytCurrentSec = val;
      this.cdr.markForCheck();
    } catch {
      /* ignore */
    }
  }

  onLocalVideoLoaded(): void {
    this.workout$.pipe(take(1)).subscribe((w) => {
      const el = this.runnerVideo?.nativeElement;
      if (!el || !w?.videoUrl || w.isYoutubeVideo) return;
      this.applyVideoMute(el, w);
      // Autoplay while genuinely playing OR while the clock is held waiting for this
      // very video to start (state is momentarily "paused" but it must still play).
      // Not off screen: it plays when the app comes back.
      if ((!w.isPaused || this.workout.awaitingMediaStart) && this.visibility.isVisible()) {
        try {
          el.play();
        } catch {
          // ignore
        }
      }
    });
  }

  /** The local <video> is fetching frames it does not have: a new source, or a
   *  stall mid-play on a streamed clip. The spinner shows until it can play. */
  onLocalVideoWaiting(): void {
    this.videoLoading = true;
    this.cdr.markForCheck();
  }

  /** The local <video> has a frame to show (or gave up): the spinner goes. */
  onLocalVideoReady(): void {
    if (!this.videoLoading) return;
    this.videoLoading = false;
    this.cdr.markForCheck();
  }

  /** The local <video> reached real playback (after load + any crop-in seek) — release
   *  the activity clock that was held while it buffered. */
  onLocalVideoPlaying(): void {
    this.onLocalVideoReady();
    this.workout.notifyMediaPlaying();
    // A frame played, so whatever went wrong before is over. Reopening the one
    // recovery attempt means a URL that expires twice in a long clip is
    // recovered twice, while a genuinely dead source still stops after one try.
    this.videoRecoveryUsed = false;
  }

  /**
   * `<video>` failed. For a streaming source that is almost always the
   * presigned URL expiring — the clip outlived it, or it sat paused past the
   * lifetime. Re-mint, put the player back where it was, and carry on.
   *
   * Once per error-free stretch of playback: a source that is actually gone
   * fails again immediately, and retrying that forever would spin.
   */
  async onVideoError(video: HTMLVideoElement): Promise<void> {
    if (this.videoRecoveryUsed) return;
    this.videoRecoveryUsed = true;
    const resumeAt = video.currentTime;
    const url = await this.workout.reresolveCurrentVideo();
    if (!url) return; // nothing to play any more — the missing-media card says so
    // The service already pushed the new url into the view model, so the [src]
    // binding may have applied it first. Assigning the same value again would
    // start a SECOND load of the same file, so only assign when it differs —
    // either way the listener below catches whichever load actually runs.
    if (video.src !== url) {
      video.src = url;
      video.load();
    }
    // Restoring the position is the difference between a hiccup and starting
    // a ten-minute clip over.
    const restore = () => {
      try {
        if (resumeAt > 0) video.currentTime = resumeAt;
      } catch {
        /* metadata not ready — the crop-in seek in onVideoMeta covers it */
      }
      void video.play().catch(() => undefined);
    };
    video.addEventListener('loadedmetadata', restore, { once: true });
  }

  /** ±10s (positive = forward): seek every active medium AND the countdown together.
   *  A rewind past the unit's start crosses the boundary instead (previous rep/set/
   *  activity restarts) — the service handles that and the media realigns itself. */
  nudge(deltaSec: number): void {
    this.workout$.pipe(take(1)).subscribe((w) => {
      if (!w) return;
      if (this.workout.nudgeBy(deltaSec)) {
        this.cdr.markForCheck();
        return;
      }
      // The countdown delta is WALL seconds; media seeks in media-time → scale by rate.
      const mediaDelta = deltaSec * (w.mediaPlaybackRate ?? 1);
      if (w.isYoutubeVideo && this.ytPlayer) {
        try {
          const t = this.ytPlayer.getCurrentTime() ?? 0;
          // Stay inside the trim: a cropped clip has no playable frames outside it,
          // so seeking past either edge parks the video on black or on someone
          // else's footage while the countdown carries on.
          const lo = this.ytCropStart();
          const hi = this.ytCropEnd();
          let next = Math.max(lo, t + mediaDelta);
          if (hi > 0) next = Math.min(next, hi);
          this.ytPlayer.seekTo(next, true);
        } catch {
          /* ignore */
        }
      } else {
        const v = this.runnerVideo?.nativeElement;
        if (v && w.videoUrl && !w.isYoutubeVideo) {
          const lo = this.videoCropStart(w);
          const hi = this.videoCropEnd(w);
          let next = Math.max(lo, v.currentTime + mediaDelta);
          // `hi > v.currentTime`: a window end behind the playhead is a bad duration
          // read, not a real edge — clamping to it would throw the video back to 0.
          if (hi > v.currentTime) next = Math.min(next, hi);
          v.currentTime = next;
          this.videoCurrentSec = next;
        }
      }
      this.cdr.markForCheck();
    });
  }

  /** Toggle native fullscreen on the media element (video / YouTube iframe). */
  toggleFullscreen(): void {
    // Fullscreen the media container, not the iframe/video itself: that keeps the
    // app's overlay controls (scrubber + minimize button) inside the fullscreen
    // element so they stay visible. iOS Safari can't fullscreen a div, so fall
    // back to webkitEnterFullscreen on the <video> there (YouTube iframe has no
    // such fallback — it uses fs handled by the container on supported browsers).
    const stage = this.mediaStage?.nativeElement as
      | (HTMLElement & { webkitRequestFullscreen?: () => void })
      | undefined;
    const video = this.runnerVideo?.nativeElement as
      | (HTMLVideoElement & { webkitEnterFullscreen?: () => void })
      | undefined;
    const el = (stage ??
      this.runnerVideo?.nativeElement ??
      this.youtubeFrame?.nativeElement) as
      | (HTMLElement & {
          webkitRequestFullscreen?: () => void;
          webkitEnterFullscreen?: () => void;
        })
      | undefined;
    if (!el) return;
    const doc = document as Document & {
      webkitFullscreenElement?: Element;
      webkitExitFullscreen?: () => void;
    };
    if (doc.fullscreenElement || doc.webkitFullscreenElement) {
      (doc.exitFullscreen ?? doc.webkitExitFullscreen)?.call(doc);
    } else if (el.requestFullscreen ?? el.webkitRequestFullscreen) {
      (el.requestFullscreen ?? el.webkitRequestFullscreen)!.call(el);
    } else {
      // iOS Safari: no element-level fullscreen for a div — use the video's own.
      video?.webkitEnterFullscreen?.call(video);
    }
  }

  /** Current/total media seconds for the scrubber (YouTube or local). */
  mediaCurrentSec(w: WorkoutViewModel): number {
    return w.isYoutubeVideo ? this.ytCurrentSec : this.videoCurrentSec;
  }
  mediaDurationSec(w: WorkoutViewModel): number {
    return w.isYoutubeVideo ? this.ytDurationSec : this.videoDurationSec;
  }

  // ── Local video crop window (start/end) ───────────────────────────────────
  /** True when the activity's video is trimmed to a sub-window of the whole clip. */
  videoCropActive(w: WorkoutViewModel): boolean {
    return (w.videoCropStartSec ?? 0) > 0 || w.videoCropEndSec != null;
  }
  /** Crop start (seconds, track-absolute). */
  videoCropStart(w: WorkoutViewModel): number {
    return Math.max(0, w.videoCropStartSec ?? 0);
  }
  /** Crop end (seconds, track-absolute), clamped to the real length once known. */
  videoCropEnd(w: WorkoutViewModel): number {
    const full = this.videoDurationSec || 0;
    const end = w.videoCropEndSec ?? full;
    return full > 0 ? Math.min(end, full) : end;
  }
  /** Playable length of the crop window — also the scrubber's max. */
  videoWindowLen(w: WorkoutViewModel): number {
    if (!this.videoCropActive(w)) return this.videoDurationSec;
    return Math.max(0, this.videoCropEnd(w) - this.videoCropStart(w));
  }
  /** Current position within the crop window (for the scrubber). */
  videoWindowCurrent(w: WorkoutViewModel): number {
    if (!this.videoCropActive(w)) return this.videoCurrentSec;
    return Math.max(0, Math.min(this.videoWindowLen(w), this.videoCurrentSec - this.videoCropStart(w)));
  }

  // ── Local video seek (player slider) ──────────────────────────────────────
  /** Full clip length. A streamed <video> reports NaN/Infinity, or only what it has
   *  parsed so far, until it has buffered — so never go below the length the media
   *  row recorded, and never below how far the playhead has actually got. */
  private syncVideoDuration(video: HTMLVideoElement): void {
    const reported = Number.isFinite(video.duration) ? video.duration : 0;
    const known = this.workout.currentState?.videoDurationHintSec ?? 0;
    this.videoDurationSec = Math.max(reported, known, video.currentTime);
  }
  onVideoDurationChange(video: HTMLVideoElement): void {
    this.syncVideoDuration(video);
    this.cdr.markForCheck();
  }
  onVideoMeta(video: HTMLVideoElement): void {
    this.syncVideoDuration(video);
    // Start playback at the crop-in point rather than 0, at the media's speed.
    const w = this.workout.currentState;
    this.applyLocalVideoRate(video, w);
    const start = w ? this.videoCropStart(w) : 0;
    if (start > 0 && video.currentTime < start) {
      try { video.currentTime = start; } catch { /* seek before ready */ }
    }
    this.cdr.markForCheck();
  }
  onVideoTime(video: HTMLVideoElement): void {
    // Honour the crop window: loop back to the crop-in point at crop-out (native
    // loop is disabled when cropped — it would rewind to 0, not the crop start).
    const w = this.workout.currentState;
    if (w && this.videoCropActive(w)) {
      const end = this.videoCropEnd(w);
      if (end > 0 && video.currentTime >= end - 0.05) {
        try { video.currentTime = this.videoCropStart(w); } catch { /* ignore */ }
      }
    }
    if (video.currentTime > this.videoDurationSec) this.syncVideoDuration(video);
    this.videoCurrentSec = video.currentTime;
    this.cdr.markForCheck();
  }

  /** Apply a media-position change to the workout clock. Media seconds convert to
   *  wall-clock seconds at the activity's playback rate, which is the same rule as
   *  the ±10-second transport controls. Returns true when the seek moves to a new
   *  unit, whose media is restarted by the normal state transition. */
  private updateCountdownForMediaSeek(fromMediaSec: number, toMediaSec: number): boolean {
    const w = this.workout.currentState;
    if (!w) return false;
    const deltaSec = (toMediaSec - fromMediaSec) / (w.mediaPlaybackRate ?? 1);
    return deltaSec !== 0 && this.workout.nudgeBy(deltaSec);
  }

  seekVideo(ev: Event, video: HTMLVideoElement): void {
    const val = Number((ev as CustomEvent<{ value: number }>).detail?.value);
    if (!Number.isFinite(val)) return;
    const w = this.workout.currentState;
    const abs = w && this.videoCropActive(w) ? this.videoCropStart(w) + val : val;
    if (this.updateCountdownForMediaSeek(video.currentTime, abs)) {
      this.cdr.markForCheck();
      return;
    }
    video.currentTime = abs;
    this.videoCurrentSec = abs;
    this.cdr.markForCheck();
  }

  /** Seek the player audio via the slider (0..1 fraction of the track). */
  onSeekAudio(ev: Event): void {
    const val = Number((ev as CustomEvent<{ value: number }>).detail?.value);
    if (Number.isFinite(val)) this.workout.seekAudio(val);
  }

  // ── Media tap gestures ─────────────────────────────────────────────────────
  // A single tap pauses/resumes; a double tap on the left or right half seeks
  // ∓10s, exactly as the ±10 buttons do. Both live on the same overlay, so the
  // first tap is HELD for DOUBLE_TAP_MS: if a second lands it becomes a seek and
  // the pause never happens, otherwise the pause fires on the timeout. Toggling
  // pause first and undoing it on the second tap would stutter the video and
  // the countdown every time somebody seeks.

  /** Pending single-tap (pause/resume) timer, cleared when a double tap wins. */
  private tapTimer: ReturnType<typeof setTimeout> | null = null;
  /** Direction of the last double-tap seek, driving the on-video flash. */
  protected seekFlash: 'back' | 'forward' | null = null;

  /** The local video has no frame to show yet (loading, or rebuffering mid-play). */
  protected videoLoading = false;
  protected readonly videoBlankPoster = VIDEO_BLANK_POSTER;
  private seekFlashTimer: ReturnType<typeof setTimeout> | null = null;
  protected readonly seekStepSec = SEEK_STEP_SEC;

  onMediaTap(event: MouseEvent, isPaused: boolean): void {
    if (this.tapTimer != null) {
      // Second tap inside the window: this is a double tap, not two taps.
      clearTimeout(this.tapTimer);
      this.tapTimer = null;
      this.seekFromTap(event);
      return;
    }
    this.tapTimer = setTimeout(() => {
      this.tapTimer = null;
      if (isPaused) this.resume();
      else this.pause();
    }, DOUBLE_TAP_MS);
  }

  /** Seek by which half of the media was tapped: left rewinds, right advances.
   *  Measured against the tapped overlay itself, so it stays correct in
   *  fullscreen and on a letterboxed video. */
  private seekFromTap(event: MouseEvent): void {
    const rect = (event.currentTarget as HTMLElement | null)?.getBoundingClientRect();
    // No geometry (synthetic event): treat it as a forward seek rather than
    // swallowing the gesture.
    const forward = !rect || event.clientX >= rect.left + rect.width / 2;
    this.nudge(forward ? SEEK_STEP_SEC : -SEEK_STEP_SEC);
    this.flashSeek(forward ? 'forward' : 'back');
  }

  private flashSeek(direction: 'back' | 'forward'): void {
    this.seekFlash = direction;
    if (this.seekFlashTimer != null) clearTimeout(this.seekFlashTimer);
    this.seekFlashTimer = setTimeout(() => {
      this.seekFlash = null;
      this.seekFlashTimer = null;
      this.cdr.markForCheck();
    }, SEEK_FLASH_MS);
    this.cdr.markForCheck();
  }

  /**
   * Left/right arrows seek the workout ∓10s while the player is open.
   *
   * Skipped while minimized (the media is not on screen, and the keys belong to
   * whatever page is) and while the user is typing or working a control that
   * owns the arrows itself — a range slider, a select, or any text field —
   * otherwise renaming an activity with the player open would jump the video.
   */
  onDocumentKeydown(event: KeyboardEvent): void {
    if (this.minimized) return;
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    if (event.metaKey || event.ctrlKey || event.altKey) return;
    if (this.isTypingTarget(event.target)) return;
    event.preventDefault();
    const forward = event.key === 'ArrowRight';
    this.nudge(forward ? SEEK_STEP_SEC : -SEEK_STEP_SEC);
    this.flashSeek(forward ? 'forward' : 'back');
    this.cdr.markForCheck();
  }

  /** True when the event target is a control that owns the arrow keys. */
  private isTypingTarget(target: EventTarget | null): boolean {
    const el = target as HTMLElement | null;
    if (!el?.tagName) return false;
    const tag = el.tagName.toLowerCase();
    return (
      tag === 'input' ||
      tag === 'textarea' ||
      tag === 'select' ||
      tag.startsWith('ion-input') ||
      tag === 'ion-textarea' ||
      tag === 'ion-searchbar' ||
      tag === 'ion-range' ||
      tag === 'ion-select' ||
      el.isContentEditable
    );
  }

  pause(): void {
    void this.workout.pause();
    // Act on the local <video> immediately so the tap reliably pauses it,
    // instead of waiting on the reactive sync (which can race with timer ticks).
    this.runnerVideo?.nativeElement?.pause();
  }
  resume(): void {
    void this.workout.resume();
    const el = this.runnerVideo?.nativeElement;
    if (el) {
      this.applyVideoMute(el, this.workout.currentState);
      void el.play().catch(() => {});
    }
  }
  /**
   * Silence the outgoing activity's on-screen media before a skip. The service
   * tears its audio slot down synchronously, but loading the next activity is
   * async (DB read + blob decode), and until its state emits, the <video>/YouTube
   * embed on screen keeps playing — so the old clip ran on over the new activity.
   * The YouTube player is PAUSED, never destroyed: a rebuild waits on the iframe's
   * load event, which does not re-fire when the same iframe stays in the DOM.
   */
  private stopCurrentMedia(): void {
    const v = this.runnerVideo?.nativeElement;
    if (v) {
      try {
        v.pause();
      } catch {
        /* element mid-teardown */
      }
    }
    try {
      this.ytPlayer?.pauseVideo();
    } catch {
      /* player mid-teardown */
    }
  }

  skipToFirst(): void {
    this.stopCurrentMedia();
    void this.workout.skipToFirst();
  }
  skipToPrev(): void {
    this.stopCurrentMedia();
    void this.workout.skipToPrev();
  }
  skipToNext(): void {
    this.stopCurrentMedia();
    void this.workout.skipToNext();
  }
  skipToLast(): void {
    this.stopCurrentMedia();
    void this.workout.skipToLast();
  }

  /** Stop and reset workout; panel closes (handled by app when workout$ becomes null). */
  stop(): void {
    void this.workout.stop();
  }

  /** Stop workout and close panel. */
  close(): void {
    void this.workout.stop();
    this.closePanel.emit();
  }

  /** Minimize panel so user can navigate.
   *  For YouTube activities the iframe is destroyed on minimize so the video stops; the
   *  workout pauses the step itself once it is told the player is no longer shown. */
  minimize(): void {
    // Save playback position first (ytPlayer is still valid at this point)
    if (this.ytPlayer) {
      try {
        this.ytCurrentTime = Math.floor(this.ytPlayer.getCurrentTime() ?? 0);
      } catch {
        this.ytCurrentTime = 0;
      }
    }
    this.minimizePanel.emit();
  }

  /**
   * Desktop only: clicking anywhere outside the expanded side panel minimises it
   * (same effect as the back button), so it behaves like a dismissible overlay
   * instead of permanently occupying the right edge. The listener is NON-blocking
   * — the click still reaches whatever was clicked (e.g. a tab navigates AND the
   * player minimises). On mobile the panel is full-screen, so there is no
   * "outside" and this is skipped.
   */
  onDocumentClick(event: MouseEvent): void {
    if (this.minimized || this.isMobile) return;
    // Ignore the same click that opened/expanded the panel.
    if (Date.now() - this.expandedAt < OUTSIDE_CLICK_GUARD_MS) return;
    const panel = this.panelEl?.nativeElement;
    const target = event.target as Node | null;
    if (!panel || !target || panel.contains(target)) return;
    this.minimize();
    this.cdr.markForCheck();
  }

  /** Expand minimized bar back to full panel. */
  reopen(): void {
    this.reopenPanel.emit();
  }

  /**
   * Android hardware/gesture back while the workout player is open. When the
   * panel is expanded, collapse it to the mini-bar first (the workout keeps
   * running) instead of letting the event fall through and exit the app or
   * navigate the page underneath. Once minimized, defer to the next handler so a
   * second back behaves normally (page back / app exit). Priority sits above the
   * page handlers (101) so the player wins while it is on screen. The
   * subscription is torn down with the rest in ngOnDestroy when the panel closes.
   * Native-only — the backButton event never fires on web.
   */
  private initPlayerBackButton(): void {
    if (!Capacitor.isNativePlatform()) return;
    this.sub.add(
      this.platform.backButton.subscribeWithPriority(
        110,
        (processNextHandler: () => void) => {
          if (!this._minimized) {
            this.minimize();
          } else {
            processNextHandler();
          }
        },
      ),
    );
  }

  /** For YouTube activities: resume the workout first (so isPaused=false is read by the re-expand logic),
   *  then open the full panel so the video plays from where it left off. The workout starts the step
   *  as the player opens, held until the reloaded video plays, so the clock and video start together. */
  reopenAndPlay(): void {
    this.workout.playWhenPlayerOpens();
    this.reopenPanel.emit();
  }

  /**
   * Swipe down on the full panel minimises it (like the chevron). Only a touch
   * that starts with the panel scrolled to the top can be one — otherwise the
   * same finger movement is the user scrolling back up — and never one that
   * starts on a control with its own drag (the seek bars).
   */
  onTouchStart(e: TouchEvent): void {
    this.swipeStart = null;
    const touch = e.changedTouches?.[0];
    if (!touch) return;
    if ((this.panelEl?.nativeElement.scrollTop ?? 0) > 0) return;
    const target = e.target as Element | null;
    if (target?.closest?.(SWIPE_IGNORE_SELECTOR)) return;
    this.swipeStart = { x: touch.clientX, y: touch.clientY };
  }

  onTouchEnd(e: TouchEvent): void {
    const start = this.swipeStart;
    this.swipeStart = null;
    const touch = e.changedTouches?.[0];
    if (!start || !touch) return;
    const dy = touch.clientY - start.y;
    const dx = Math.abs(touch.clientX - start.x);
    if (dy >= SWIPE_MINIMIZE_MIN_PX && dy >= dx * SWIPE_MINIMIZE_VERTICAL_RATIO) {
      this.minimize();
    }
  }

  /** The OS took the touch (e.g. a system gesture); it is not a swipe. */
  onTouchCancel(): void {
    this.swipeStart = null;
  }
}
